import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFormattedDate } from '../utils';

export enum NotificationType {
  ERROR = 'danger',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
}

type Frequency = 'daily' | 'weekly' | 'monthly';

export interface INotification {
  id?: number;
  type: NotificationType;
  title?: string;
  content: JSX.Element | string;
  dissmissOptions?: INotificationDismiss;
}

type INotificationDismiss = {
  key: string;
  autoDismiss?: boolean;
  timeout?: number;
  frequency: Frequency;
};

interface INotificationProps {
  notification: INotification;
}

interface INotificationsContainerProps {
  notifications: INotification[];
}

interface INotificationContext {
  addNotification: (options: INotification) => number;
  removeNotification: (id: number) => void;
  notifications: INotification[];
}

const getDaysForFrequency = (frequency: Frequency): number => {
  switch (frequency) {
    case 'daily':
      return 1;
    case 'weekly':
      return 7;
    case 'monthly':
      return 30;
    default:
      return 1;
  }
};

const Notification: React.FC<INotificationProps> = ({ notification }) => {
  const { removeNotification } = useNotification();
  const id = notification.id ?? Date.now();
  const dissmissOptions = notification.dissmissOptions;

  useEffect(() => {
    if (dissmissOptions && !dissmissOptions.autoDismiss) {
      return;
    }

    const duration =
      dissmissOptions && dissmissOptions.timeout
        ? dissmissOptions.timeout
        : notification.type === NotificationType.ERROR || notification.type === NotificationType.WARNING
        ? 5000
        : 3000;
    const timer = setTimeout(() => {
      removeNotification(id);
    }, duration);
    return () => clearTimeout(timer);
  }, [notification, removeNotification]);

  return (
    <div role="alert" className={`notification notification-${notification.type} fade show my-1`}>
      <div className="notification-header">
        <div className="d-flex w-100 justify-content-between">
          {(function () {
            if (notification.title) {
              return notification.title;
            }
            switch (notification.type) {
              case 'danger':
                return 'An error occurred';
              case 'info':
                return 'Information';
              case 'success':
                return 'Operation successful';
              default:
                return 'Warning';
            }
          })()}
          {dissmissOptions && dissmissOptions.autoDismiss === false && (
            <button type="button" onClick={() => removeNotification(id)} className="btn-close"></button>
          )}
        </div>
      </div>
      <div className="notification-body">{notification.content}</div>
    </div>
  );
};

const NotificationsContainer: React.FC<INotificationsContainerProps> = ({ notifications }) => {
  return createPortal(
    <div
      style={{
        position: 'fixed',
        zIndex: 1051, // to work with modals
        bottom: '50px',
        right: '25px',
      }}>
      {notifications.map((notification) => (
        <Notification key={notification.id} notification={notification} />
      ))}
    </div>,
    document.body
  );
};

export const NotificationContext = createContext<INotificationContext>({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addNotification: (_options: INotification) => 0,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeNotification: (_id: number) => null,
  notifications: [],
});

export const NotificationContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<INotification[]>([]);

  const addNotification = useCallback((options: INotification) => {
    const { type, content, title, dissmissOptions } = options;
    const today = getFormattedDate(new Date()).full();
    const id = Date.now();

    if (dissmissOptions && dissmissOptions.frequency) {
      const notificationKey = `notification-${dissmissOptions.key}`;
      const notificationData = localStorage.getItem(`notification-${dissmissOptions.key}`);
      const notificationInfo = notificationData ? JSON.parse(notificationData) : undefined;

      if (notificationInfo && notificationInfo.date) {
        const lastShownDate = new Date(notificationInfo.date);
        const daysElapsed = (new Date(today).getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24);

        const isGoodForDate = daysElapsed >= getDaysForFrequency(dissmissOptions.frequency);

        if (!isGoodForDate) {
          return id;
        }

        const info = { date: today };
        localStorage.setItem(notificationKey, JSON.stringify(info));
      }
    }

    setNotifications((notifications) => [
      ...notifications,
      {
        id,
        type,
        content,
        title,
        dissmissOptions,
      } as INotification,
    ]);

    return id;
  }, []);

  const removeNotification = useCallback(
    (id: number) => {
      const notificationToRemove = notifications.find((n) => n.id === id);
      const today = getFormattedDate(new Date()).full();

      if (notificationToRemove && notificationToRemove.dissmissOptions) {
        const dissmissOptions = notificationToRemove.dissmissOptions;
        if (!dissmissOptions.frequency) return;
        const notification = localStorage.getItem(`notification-${dissmissOptions.key}`);
        const notificationInfo = notification ? JSON.parse(notification) : { date: today };
        notificationInfo.date = today;
        localStorage.setItem(`notification-${dissmissOptions.key}`, JSON.stringify(notificationInfo));
      }
      setNotifications((prev) => prev.filter((t) => t.id !== id));
    },
    [notifications]
  );

  return (
    <NotificationContext.Provider value={{ addNotification, removeNotification, notifications }}>
      <NotificationsContainer notifications={notifications} />
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotification(): INotificationContext {
  return useContext(NotificationContext);
}
